import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { Box, ButtonBase, Checkbox, FormControlLabel, Grid, makeStyles, Typography } from '@material-ui/core';
import { connect, useSelector } from 'react-redux';
import moment from 'moment';
import * as Yup from 'yup';
import _ from 'lodash';
import { bedStatusAPI } from '../../../../apis';
import { ColumnItem } from '../../../../components';
import AutoCompleteMultiCheckbox from '../../../../components/AutoCompleteMultiCheckbox';
import {
  DateTimeIcon,
  DocIcon,
  PendingFYAIcon,
  CloseIcon,
  OutstandingIcon,
  DropUpIcon,
  DropDownIcon,
} from '../../../../components/Icon';
import { i18n } from '../../../../language';
import { classNames, downloadFileById, getUserInfo, isRequest } from '../../../../utils';
import { checkSubmitData } from '../../../../utils/check';
import { alertMsg, ResponseCommCodeEnum } from '../../../../apis/commlogic';
import { BEDSTATUS_OUTSTANDING_TRANSFER_IN } from '../../../../comm/constant';
import TransferOutPop from '../PopWindows/TransferOutPop';
import { LoginPop } from '../../../../components/Login';
import { useActionableTicketReminder } from '../../../../utils/hooks';
import { initEndDate, initStartDate } from '../InternalBooking/Admission';
import exportPwdExcel from '../../../../utils/exportPwdExcel';
import { BedStatusStatisticContext } from '../..';
import { baseCssBooking } from '../PopWindows/baseCss';
import { revampTableList } from '../../../../utils/newBedStatusUtil';
import DateFilter from './DateFilter';
import TransferOutTable from './TransferOutTable';

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#fff',
    borderRadius: '0 0.937rem 0.937rem 0.937rem',
    overflowY: 'auto',
    margin: '0 3.375rem 0',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem 1.875rem',
    borderBottom: 'solid 1px #ddd',
  },
  bookColumn: {
    flexBasis: 'unset',
    padding: '1.844rem 1.875rem 1.875rem',
  },
  select: {
    width: '9.375rem',
    height: '2.5rem',
    opacity: 0.85,
    flexGrow: 0,
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: '0.5rem',
      boxShadow: '-1px 4px 10px 0 rgba(221, 232, 251, 0.58)',
      border: 'solid 1px #ddd',
    },
    '& .MuiSelect-root': {
      lineHeight: '2.5rem',
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#2e4469',
    },
  },
  filter: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#2e4469',
    whiteSpace: 'nowrap',
  },
  ward: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2e4469',
  },
  newBooking: {
    width: '10rem',
    height: '3rem',
    borderRadius: '0.625rem',
    background: '#538fff',
    fontSize: '1rem',
    fontWeight: '500',
    fontFamily: 'NotoSans-Medium',
    color: '#fff',
  },
  dateTimeIcon: {
    fontSize: '1.25rem',
    marginRight: '0.188rem',
  },
  columnItem: {
    width: '8.925rem',
    margin: '0 2rem',
    [theme.breakpoints.only('xs')]: {
      width: '100%',
    },
    marginLeft: '0.5rem',
    whiteSpace: 'nowrap',
  },
  columnBox: {
    display: 'flex',
    justifyContent: 'start',
    flexWrap: 'wrap',
    margin: '0 1.875rem 1rem 1.875rem',
    padding: '0.175rem 0rem 0.175rem 0.175rem',
    borderRadius: '0.937rem',
    border: '1.2px solid rgb(228, 231, 235)',
    alignItems: 'center',
  },
  filterBox: {
    // display: 'flex',
    // flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '1rem',
    paddingLeft: '1.875rem',
    borderBottom: 'solid 1px #ddd',
  },
  errorMsg: {
    margin: '3px 0.825rem 0 0.825rem',
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  filterStatus: {},
  filterStatusTitle: {
    fontSize: '1rem',
    color: '#2e4469',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
    marginRight: '0.625rem',
  },
  detailBtn: ({ detailOpen }) => ({
    fontFamily: 'NotoSans-Medium',
    color: detailOpen ? '#7d86a9' : '#fff',
    fontSize: '1rem',
    width: '11.25rem',
    minWidth: '11.25rem',
    height: '2rem',
    position: 'absolute',
    top: '-1rem',
    left: '1.75rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '4px',
    zIndex: '999',
    backgroundColor: detailOpen ? '#fff' : '#7d86a9',
  }),
  dropIcon: {
    fontSize: '0.875rem',
    marginLeft: '1rem',
  },
  datepicker: {
    '& .MuiInputBase-root': {
      borderRadius: '0.5rem',
      backgroundColor: theme.palette.font.white,
      borderColor: '#e4e7eb',
      paddingLeft: '0.3rem',
    },
    '& .MuiInputBase-input': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '0.919rem',
      letterSpacing: '0',
      color: theme.palette.font.sub,
      fontWeight: '500',
      marginLeft: '-0.3rem',
      width: '96px',
      paddingRight: '0',
    },
  },
  disabledStyle: {
    '& .MuiInputBase-root': {
      backgroundColor: '#f5f5f5',
    },
  },
}));

function TransferOut({ loginInfo, userRole, rouList }) {
  const [, needRefresh] = useActionableTicketReminder('msg', 'BS');
  const outstanding = useMemo(() => {
    const session = sessionStorage.getItem(BEDSTATUS_OUTSTANDING_TRANSFER_IN);
    let result = null;
    try {
      result = JSON.parse(session);
    } catch {}
    sessionStorage.removeItem(BEDSTATUS_OUTSTANDING_TRANSFER_IN);
    return result;
  }, []);
  const outstandingArr = outstanding?.status?.map((item) => ({ value: item }));
  const [checkDataFlag, setCheckDataFlag] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const classes = useStyles({ detailOpen });
  const baseClasses = baseCssBooking();
  const columnTitles = [i18n.t('BEDSTATUS_PENDING'), i18n.t('BEDSTATUS_ACCEPTED'), i18n.t('BEDSTATUS_DECLINED')];
  const iconBgColors = ['#ffc95c', '#5cd9f4', '#ff655c'];
  const tableRef = useRef(null);
  const PAGESIZE = 20;
  const language = useSelector((state) => state.settings.language);
  const [transferOutStatus, setTransferOutStatus] = useState(outstanding ? outstandingArr : []);
  const transferOutStatusList = [
    { text: i18n.t('BEDSTATUS_PENDING'), value: 'P' },
    { text: i18n.t('BEDSTATUS_ACCEPTED'), value: 'A' },
    { text: i18n.t('BEDSTATUS_DECLINED'), value: 'DL' },
    { text: i18n.t('BEDSTATUS_RECV_WARD_READY'), value: 'RWR' },
    { text: i18n.t('BEDSTATUS_COMPLETED_SINCE_24HOURS_AGO'), value: 'C' },
    { text: i18n.t('BEDSTATUS_DELETED'), value: 'D' },
  ];
  const [loginPopup, setLoginPopup] = useState(false);
  const isLogin = useSelector((state) => state.loginInfo.flag);
  const { refresh: refresh_btn } = useContext(BedStatusStatisticContext);

  const onChangeHandle = useCallback((key) => {
    return (event) => {
      if (key === 'select') {
        setTransferOutStatus(event);
      }
    };
  }, []);
  const [isNewBookingPop, setIsNewBookingPop] = useState(false);
  const closePop = () => {
    setIsNewBookingPop(false);
  };
  const showPop = () => {
    if (isLogin) {
      setIsNewBookingPop(true);
    } else {
      setLoginPopup(true);
    }
  };

  const [startDate, setStartDate] = useState(initStartDate(outstanding));
  const [stopDate, setStopDate] = useState(initEndDate(outstanding));
  const [AdmDateTbc, setAdmDateTbc] = useState(false);
  const setDate = (type) => {
    return (event) => {
      if (type === 'start') {
        setStartDate(event);
      } else if (type === 'stop') {
        setStopDate(event);
      }
    };
  };

  const [BsBookingList, setBsBookingList] = useState([]);
  const [BsMlookupList, setBsMlookupList] = useState(null);
  const [count, setCount] = useState(null);
  const getBsTransferOutRequestlist = async (pageSize, pageNo) => {
    try {
      const { result } = await bedStatusAPI.getBsTransferOutRequestlist({
        startDate: startDate ? moment(startDate.format('yyyy-MM-DD 00:00:00')).valueOf() : null,
        endDate: stopDate ? moment(stopDate.format('yyyy-MM-DD 23:59:59')).valueOf() : null,
        nonDate: null,
        pageNo: pageNo,
        pageSize: pageSize,
        status:
          transferOutStatus.length === 5 && transferOutStatus.length === 0
            ? null
            : transferOutStatus.map((i) => i.value).join(','),
      });
      setCount(result?.count ?? 0);
      setColumnData([
        { ...columnData[0], text: result?.pendingCount ?? 0 },
        { ...columnData[1], text: result?.acceptedCount ?? 0 },
        { ...columnData[2], text: result?.declinedCount ?? 0 },
      ]);
      // Decrypt HKIDs (revampTableList returns the resolved array)
      const decryptedData = await revampTableList(result.data);
      const newBsBookingList = [...BsBookingList.slice(0, (pageNo - 1) * pageSize), ...decryptedData];
      setBsBookingList(newBsBookingList);
      return decryptedData;
    } catch (e) {
      console.log('-------------', e);
    }
  };

  const [errorMsg, setErrorMsg] = useState(null);
  const schema = {
    startDate: stopDate
      ? Yup.date()
          .max(
            stopDate,
            i18n.t('ALERT_START_DATE_CANNOT_BE_LATER_THAN_END_DATE_CANNOT_BE_LATE_THAN_ENDDATE', {
              value: startDate?.format('DD-MM-yyyy'),
            })
          )
          .min(
            moment(stopDate).subtract(100, 'days'),
            i18n.t('BEDSTATUS_START_DATE_AND_END_DATE_CANNOT_BE_OVER_100_DAYS')
          )
          .typeError(i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') }))
      : Yup.date().typeError(i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') })),
    stopDate: Yup.date().typeError(
      i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_END_DATE') })
    ),
  };

  useEffect(() => {
    (async () => {
      if (checkDataFlag) {
        try {
          await checkSubmitData(Yup.object().shape(schema), { startDate, stopDate });
          setErrorMsg({ ...errorMsg, startDate: null, stopDate: null });
        } catch (e) {
          setErrorMsg({ ...errorMsg, ...e });
        }
      }
    })();
  }, [language]);

  useEffect(() => {
    (async () => {
      try {
        await checkSubmitData(Yup.object().shape(schema), { startDate, stopDate });
        await getBsTransferOutRequestlist(PAGESIZE, 1);
        tableRef?.current?.scrollTo(0, 0);
        setErrorMsg({ ...errorMsg, startDate: null, stopDate: null });
      } catch (e) {
        setErrorMsg({ ...errorMsg, ...e });
      }
    })();
  }, [startDate, stopDate, tableRef, transferOutStatus, needRefresh, refresh_btn, rouList, AdmDateTbc]);

  useEffect(() => {
    if (isRequest(loginInfo, userRole)) {
      setCount(null);
      setBsBookingList([]);
    }
  }, [rouList]);

  const [columnData, setColumnData] = useState([
    { id: 0, text: 0, IconCom: PendingFYAIcon },
    { id: 1, text: 0, IconCom: OutstandingIcon },
    { id: 2, text: 0, IconCom: CloseIcon },
  ]);

  const [hideIndex, setHideIndex] = useState(null);
  const addSaveHandle = async (requestVal) => {
    try {
      const { result } = await bedStatusAPI.postCreateBsTransferOutRequest({ ...requestVal, wardId: loginInfo.wardId });
      tableRef.current.scrollTo(0, 0);
      const temp = await getBsTransferOutRequestlist(PAGESIZE, 1);
      setHideIndex(temp?.findIndex((item) => item.wardId === result.id));
      closePop();
    } catch (error) {
      if (error.statusCode === ResponseCommCodeEnum.BED_VERSION) {
        alertMsg(
          i18n.t(
            'BEDSTATUS_THE_TRANSFER_IN_DETAIL_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_PLEAS_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_TRANSFER_IN_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
          ),
          _.uniqueId('warning_'),
          'warning'
        );
      } else {
        console.log(`---------${error}------------`);
      }
    }
  };

  const editSaveHandle = async (requestVal, editIndex) => {
    try {
      await bedStatusAPI.postUpdateBsTransferOutRequest(requestVal);
      const pageNo = Math.ceil((editIndex + 1) / PAGESIZE);
      const temp = await getBsTransferOutRequestlist(PAGESIZE, pageNo);
      const index = temp?.findIndex((item) => item.transferRequestId === requestVal.transferRequestId);
      setHideIndex(index > -1 ? (pageNo - 1) * PAGESIZE + index : index);
      return true;
    } catch (error) {
      if (error.statusCode === ResponseCommCodeEnum.BED_VERSION) {
        alertMsg(
          i18n.t(
            'BEDSTATUS_THE_TRANSFER_IN_DETAIL_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_PLEAS_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_TRANSFER_IN_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
          ),
          _.uniqueId('warning_'),
          'warning'
        );
      } else {
        console.log(`---------${error}------------`);
      }
    }
  };

  const exportExcelHandle = async (protectKey) => {
    await downloadFileById(
      () => {
        return bedStatusAPI.getExportBsTransferRequestOutInfo({
          wardId: loginInfo.wardId,
          startDate: startDate ? moment(startDate.format('yyyy-MM-DD 00:00:00')).valueOf() : null,
          endDate: stopDate ? moment(stopDate.format('yyyy-MM-DD 23:59:59')).valueOf() : null,
          nonDate: null,
          status:
            transferOutStatus.length === 5 && transferOutStatus.length === 0
              ? null
              : transferOutStatus.map((i) => i.value).join(','),
          protectKey,
        });
      },
      (res) => {
        return `bedstatus Export Excel_${moment().valueOf()}`;
      }
    );
  };

  const getWardName = () => {
    const userInfo = getUserInfo();
    return userInfo?.wardName ?? '';
  };

  const handleOpenDetail = () => {
    setDetailOpen(!detailOpen);
  };

  const nextPageCall = async (pageSize, pageNo) => {
    try {
      if (count === null || count !== BsBookingList.length) {
        if (count === null) {
          const getBsMlookupList = await bedStatusAPI.getBsMlookupList();
          setBsMlookupList(getBsMlookupList.result);
        }
        if (pageNo !== 1) {
          await getBsTransferOutRequestlist(pageSize, pageNo);
        }
      }
    } catch (error) {
      console.log('-----Ward error-----', error);
    }
  };

  // Page auto refresh (every 5 mins)
  useEffect(() => {
    const timer = setInterval(() => {
      (async () => {
        await getBsTransferOutRequestlist(PAGESIZE, 1);
      })();
    }, 60 * 1000);
    return () => {
      clearInterval(timer);
    };
  }, [startDate, stopDate, transferOutStatus, needRefresh, refresh_btn, AdmDateTbc]);
  return (
    <div className={classes.root}>
      <div>
        <div className={classes.filterBox}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              {/* <Box>
                <FormControlLabel
                  id="bed_transferpop_on_oxygen"
                  control={<Checkbox />}
                  label="Adm. Date"
                  className={baseClasses.checkBox}
                  checked={AdmDateTbc}
                  onChange={(event) => setAdmDateTbc(event.target.checked)}
                />
              </Box> */}
              <Box>
                <DateFilter
                  setStartDate={(val) => {
                    setDate('start')(val);
                    setCheckDataFlag(true);
                  }}
                  setEndDate={(val) => {
                    setDate('stop')(val);
                    setCheckDataFlag(true);
                  }}
                  startDate={startDate}
                  stopDate={stopDate}
                  errorMsg={{
                    startDate:
                      errorMsg?.startDate ===
                      i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') })
                        ? i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') })
                        : null,
                    stopDate: errorMsg?.stopDate,
                  }}
                  startDatePickProps={{
                    disabled: AdmDateTbc,
                    className: classNames(classes.datepicker, AdmDateTbc && classes.disabledStyle),
                  }}
                  stopDatePickProps={{
                    disabled: AdmDateTbc,
                    className: classNames(classes.datepicker, AdmDateTbc && classes.disabledStyle),
                  }}
                />
                {errorMsg?.startDate !==
                  i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') }) &&
                errorMsg?.startDate ? (
                  <Typography className={classes.errorMsg}>{errorMsg?.startDate ?? ''}</Typography>
                ) : (
                  ''
                )}
              </Box>
              <Box display="flex" justifyContent="center" alignItems="center" ml="1.6rem">
                <div className={classes.filterStatusTitle}>{i18n.t('BEDSTATUS_STATUS')}: </div>
                <AutoCompleteMultiCheckbox
                  id="bed_transferOut_status"
                  itemList={transferOutStatusList}
                  placeholder={i18n.t('BEDSTATUS_SELECT_FROM_BELOW_ITEMS')}
                  value={transferOutStatus}
                  onChange={onChangeHandle('select')}
                  className={classes.filterStatus}
                  height="2.6rem"
                  width="12.5rem"
                />
              </Box>
            </Box>
            <Box display="flex">
              <ButtonBase
                id="bed_excel_btn1"
                className={classes.newBooking}
                style={{ marginRight: '1.25rem' }}
                onClick={() => {
                  if (!isLogin) {
                    setLoginPopup(true);
                    return;
                  }
                  exportPwdExcel(exportExcelHandle)();
                }}
              >
                <DocIcon className={classes.dateTimeIcon} />
                <Typography>{i18n.t('BEDSTATUS_EXPORT_EXCEL')}</Typography>
              </ButtonBase>
              <ButtonBase id="bed_new_btn1" className={classes.newBooking} onClick={showPop}>
                <DateTimeIcon className={classes.dateTimeIcon} />
                <Typography>{i18n.t('BEDSTATUS_NEW_REQUEST')}</Typography>
              </ButtonBase>
            </Box>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {detailOpen && (
              <Box display="flex" alignItems="center" marginTop="1rem">
                <Typography className={classes.ward}>{getWardName()}</Typography>
                <div style={{ flexDirection: 'column', display: 'flex' }}>
                  <div className={classes.columnBox}>
                    <Grid container flex="1">
                      {columnData.map((item, index) => {
                        return (
                          <ColumnItem
                            key={item.id}
                            {...item}
                            title={columnTitles[index]}
                            iconBgColor={iconBgColors[index]}
                            className={classes.columnItem}
                            xs={3}
                            sm={3}
                            md={3}
                          />
                        );
                      })}
                    </Grid>
                  </div>
                </div>
              </Box>
            )}
          </Box>
        </div>
      </div>
      <div style={{ position: 'relative' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <ButtonBase className={classes.detailBtn} onClick={handleOpenDetail}>
            Statistic Detail
            {detailOpen ? <DropUpIcon className={classes.dropIcon} /> : <DropDownIcon className={classes.dropIcon} />}
          </ButtonBase>
        </Box>
      </div>
      <Box marginTop="1rem">
        <TransferOutTable
          className={classes.BookingTable}
          allData={BsBookingList}
          saveHandle={editSaveHandle}
          ref={tableRef}
          bsMlookupList={BsMlookupList}
          nextPageCall={nextPageCall}
          pageSize={PAGESIZE}
          hideIndex={hideIndex}
        />
      </Box>
      {isNewBookingPop ? (
        <TransferOutPop
          open={isNewBookingPop}
          closePop={closePop}
          title={i18n.t('BEDSTATUS_TRANSFER_OUT_BUTTON')}
          saveHandle={addSaveHandle}
          bsMlookupList={BsMlookupList}
          bsMlookupListStatus={[{ lookUpName: i18n.t('BEDSTATUS_PENDING'), lookUpValue: 'P' }]}
          disabledStatus={true}
        />
      ) : (
        ''
      )}
      {loginPopup && <LoginPop closehandle={() => setLoginPopup(false)} submitHandle={() => setLoginPopup(false)} />}
    </div>
  );
}

export default connect((state) => ({
  loginInfo: state.loginInfo,
  userRole: state.userRole,
  rouList: state.rouList,
}))(TransferOut);
